import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useChatContext } from 'stream-chat-react'
import { useUserInfo } from 'hooks/useUserInfo'
import { useGetBusinessProfile, IAssigneeOptions } from 'appHooks/useGetBusinessProfile'
import {
  useGetAdvisorDashboardQuery,
  useGetAdvisorsQuery,
  useGetBusinessesQuery,
  BusinessUserType,
} from '__generated__/api-types-and-hooks'
import { useTenantId } from 'utils/useTenantId'
import { USER_ROLES } from 'config'
import { useSelector } from 'react-redux'
import { RootState } from 'App'
import { Camera, Check, Search, Trash2 } from 'lucide-react'

import UserGroupIcon from 'assets/svgs/user-group.svg'
import { uploadFiles } from 'components/AppFileUploader/AppFileUploader.helper'
import { getInitials } from 'components/Chat/helper'
import { AppModalBox } from 'components/Common/AppModalBox/AppModalBox.component'
import { AppAvatar } from 'components/Common/AppAvatar/AppAvatar.component'

interface GroupCreationProps {
  onClose: () => void
  onGroupCreated: (channel: any) => void
  chatUsers: any
}

type TabKey = 'info' | 'members'

const GroupCreation: React.FC<GroupCreationProps> = ({ onClose, onGroupCreated }) => {
  const [tab, setTab] = useState<TabKey>('members')

  const [groupName, setGroupName] = useState('')
  const [description, setDescription] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set())
  const [users, setUsers] = useState<IAssigneeOptions[]>([])
  const [isCreating, setIsCreating] = useState(false)
  const [groupIcon, setGroupIcon] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const { client } = useChatContext()
  useUserInfo()
  const tenantId = useTenantId()
  const loggedInUser = useSelector((state: RootState) => state.user.user)
  const isBsoAdmin = loggedInUser?.roles?.includes(USER_ROLES.BSO_ADMIN)
  const isBsoAdvisor = loggedInUser?.roles?.includes(USER_ROLES.BSO_ADVISOR)

  // Get business profile to access team members (for Business Owners)
  const { assigneeOptions: businessAssigneeOptions } = useGetBusinessProfile({ enabled: true })

  // Get advisor dashboard data (for BSO_ADVISOR to see assigned clients)
  const { data: advisorData } = useGetAdvisorDashboardQuery({ tenantId })

  // BSO_ADMIN: Fetch all businesses (clients) in tenant
  const { data: allBusinessesData, isLoading: businessesLoading } = useGetBusinessesQuery(
    { getBusinessesInput: { tenantId, allStatuses: true } },
    { enabled: isBsoAdmin, refetchOnWindowFocus: false }
  )

  // BSO_ADMIN & BSO_ADVISOR: Fetch all advisors in tenant
  const { data: advisorsData, isLoading: advisorsLoading } = useGetAdvisorsQuery(
    { getAdvisorsInput: { tenantId } },
    { enabled: isBsoAdmin || isBsoAdvisor, refetchOnWindowFocus: false }
  )

  // Calculate if we're still loading users
  const isLoadingUsers = useMemo(() => {
    if (isBsoAdmin) {
      return businessesLoading || advisorsLoading
    } else if (isBsoAdvisor) {
      return advisorsLoading
    }
    return false
  }, [isBsoAdmin, isBsoAdvisor, businessesLoading, advisorsLoading])

  // Memoize assignee options to prevent infinite re-renders
  const assigneeOptions = useMemo(() => {
    let options: IAssigneeOptions[] = []

    if (isBsoAdmin) {
      // BSO_ADMIN: All businesses (clients) + all advisors in tenant
      const businessesResponse = JSON.parse(allBusinessesData?.getBusinesses?.data || '{}')
      const businessesList = businessesResponse?.list || []
      const businesses = businessesList
        .filter((b: any) => b.id && b.firstName && b.lastName)
        .map((b: any) => ({
          label: `${b.firstName} ${b.lastName}`,
          value: b.id,
          email: b.email,
          type: BusinessUserType.Member,
          user: {
            id: b.id,
            firstName: b.firstName,
            lastName: b.lastName,
            email: b.email,
          },
        }))

      const advisorsResponse = JSON.parse(advisorsData?.getAdvisors?.data || '{}')
      const advisorsList = advisorsResponse?.list || []
      const advisors = advisorsList
        .filter((a: any) => a.id && a.firstName && a.lastName)
        .map((a: any) => ({
          label: `${a.firstName} ${a.lastName}`,
          value: a.id,
          email: a.email,
          type: BusinessUserType.Member,
          user: {
            id: a.id,
            firstName: a.firstName,
            lastName: a.lastName,
            email: a.email,
          },
          isAdvisor: true,
        }))

      // Combine and deduplicate
      options = [...businesses, ...advisors]
      const seen = new Set()
      options = options.filter((opt) => {
        if (seen.has(opt.value)) return false
        seen.add(opt.value)
        return true
      })
    } else if (isBsoAdvisor) {
      // BSO_ADVISOR: Assigned clients + all advisors
      const clientsResponse = JSON.parse(advisorData?.getAdvisorDashboard?.data || '{}')
      const clients =
        clientsResponse?.accounts?.map((account: any) => ({
          label: `${account.firstName} ${account.lastName}`,
          value: account.id,
          email: account.email,
          type: BusinessUserType.Member,
          user: {
            id: account.id,
            firstName: account.firstName,
            lastName: account.lastName,
            email: account.email,
          },
        })) || []

      const advisorsResponse = JSON.parse(advisorsData?.getAdvisors?.data || '{}')
      const advisorsList = advisorsResponse?.list || []
      const advisors = advisorsList
        .filter((a: any) => a.id && a.firstName && a.lastName)
        .map((a: any) => ({
          label: `${a.firstName} ${a.lastName}`,
          value: a.id,
          email: a.email,
          type: BusinessUserType.Member,
          user: {
            id: a.id,
            firstName: a.firstName,
            lastName: a.lastName,
            email: a.email,
          },
          isAdvisor: true,
        }))

      // Combine and deduplicate
      options = [...clients, ...advisors]
      const seen = new Set()
      options = options.filter((opt) => {
        if (seen.has(opt.value)) return false
        seen.add(opt.value)
        return true
      })
    } else {
      // BUSINESS_OWNER: Team members + assigned advisors
      options = businessAssigneeOptions
    }

    // Filter out current user
    return options.filter((opt) => opt.value !== loggedInUser?.id)
  }, [
    isBsoAdmin,
    isBsoAdvisor,
    businessAssigneeOptions,
    allBusinessesData,
    advisorsData,
    advisorData,
    loggedInUser?.id,
  ])

  useEffect(() => {
    setUsers(assigneeOptions)
  }, [assigneeOptions])

  const handleAddMember = (userId: string) => {
    const next = new Set(selectedUsers)
    next.add(userId)
    setSelectedUsers(next)
  }

  const handleRemoveMember = (userId: string) => {
    const next = new Set(selectedUsers)
    next.delete(userId)
    setSelectedUsers(next)
  }

  const handleIconClick = () => {
    fileInputRef.current?.click()
  }

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    try {
      const uploaded = await uploadFiles({
        files: [file],
        tenantId,
        clientId: '',
        userId: loggedInUser?.id,
      })
      setGroupIcon(uploaded?.[0]?.url || null)
    } catch (err) {
      console.error('Failed to upload group icon', err)
    }
  }

  const handleCreateGroup = async () => {
    if (!groupName.trim() || selectedUsers.size === 0) return

    setIsCreating(true)
    try {
      const selectedUsersIds = Array.from(selectedUsers)

      const customData: any = {
        members: [...selectedUsersIds, client.userID || 'unknown'],
        team: tenantId,
        name: groupName.trim(),
        isGroup: true,
      }

      if (description.trim()) customData.description = description.trim()
      if (groupIcon) customData.image = groupIcon

      // Use explicit channel ID so the channel is non-distinct and members can be updated later
      const channelId = `group_${tenantId}_${Date.now()}`

      const channel = client.channel('messaging', channelId, customData)
      await channel.create()
      await channel.watch()

      onGroupCreated(channel)
    } catch (error) {
      console.error('Failed to create group:', error)
    } finally {
      setIsCreating(false)
    }
  }

  const isCreateDisabled = !groupName.trim() || selectedUsers.size === 0 || isCreating

  const modalTitle = (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-3">
        <div className="grid h-9 w-9 place-items-center rounded-full bg-app-grey-30 flex-shrink-0">
          <img src={UserGroupIcon} alt="" className="h-5 w-5" aria-hidden />
        </div>
        <div>
          <div className="text-base font-semibold text-app-black-100">New group</div>
          <div className="text-xs text-app-grey-60">Create a group and add members</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-app-grey-25">
        <div className="flex gap-6 text-sm">
          <TabButton active={tab === 'info'} onClick={() => setTab('info')}>
            Info
          </TabButton>

          <TabButton active={tab === 'members'} onClick={() => setTab('members')}>
            <span className="inline-flex items-center gap-1.5">
              Members
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-app-primary-60 text-[11px] font-semibold leading-none text-app-white-100">
                {selectedUsers.size}
              </span>
            </span>
          </TabButton>
        </div>
      </div>
    </div>
  )

  return (
    <AppModalBox
      onClose={onClose}
      title={modalTitle}
      width="w-full min-w-[540px]"
      height="h-auto"
      rounded="2xl"
      backgroundColor="bg-app-white-100"
      childClass="px-5 pb-4 pt-4"
      showBorder={false}
      padding="px-5 pt-5 pb-0"
      position="center"
      closeOnBackdropClick={true}
    >
      {tab === 'info' && (
        <div className="space-y-3">
          {/* Group logo */}
          <div className="rounded-2xl border border-app-grey-25 bg-app-white-100 p-4">
            <div className="text-sm font-semibold text-app-black-100">Group icon</div>

            <div className="mt-3 flex items-start gap-4">
              <button
                type="button"
                onClick={handleIconClick}
                className="relative grid h-16 w-16 place-items-center overflow-hidden rounded-2xl border border-app-grey-25 bg-app-grey-10 hover:bg-app-grey-20"
              >
                {groupIcon ? (
                  <img src={groupIcon} alt="Group icon" className="h-full w-full object-cover" />
                ) : (
                  <Camera className="h-6 w-6 text-app-grey-60" />
                )}
              </button>

              <div className="flex flex-col gap-2">
                <button
                  type="button"
                  onClick={handleIconClick}
                  className="inline-flex items-center justify-center rounded-xl bg-app-primary-60 px-4 py-2 text-sm font-semibold text-app-white-100 hover:bg-app-primary-70"
                >
                  Upload picture
                </button>
                <button
                  type="button"
                  onClick={() => setGroupIcon(null)}
                  disabled={!groupIcon}
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-app-grey-25 bg-app-white-100 px-4 py-2 text-sm font-semibold text-app-grey-80 hover:bg-app-grey-10 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Trash2 className="h-4 w-4 text-app-grey-60" />
                  Delete
                </button>
              </div>
            </div>

            <div className="mt-3 text-xs text-app-grey-60">
              Click to update or upload a picture.
            </div>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            className="hidden"
          />

          {/* Group name */}
          <div className="rounded-2xl border border-app-grey-25 bg-app-white-100 p-4">
            <div className="text-sm font-semibold text-app-black-100">Group name</div>
            <input
              type="text"
              placeholder="Group name"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              className="mt-3 h-10 w-full rounded-xl border border-app-grey-25 bg-app-white-100 px-3 text-sm text-app-black-100 placeholder:text-app-grey-50 outline-none focus:border-app-primary-60 focus:ring-2 focus:ring-app-primary-10"
            />
          </div>

          {/* Description */}
          <div className="rounded-2xl border border-app-grey-25 bg-app-white-100 p-4">
            <div className="text-sm font-semibold text-app-black-100">Description</div>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional: what is this group for?"
              rows={4}
              className="mt-3 w-full resize-none rounded-xl border border-app-grey-25 bg-app-white-100 px-3 py-2 text-sm text-app-black-100 placeholder:text-app-grey-50 outline-none focus:border-app-primary-60 focus:ring-2 focus:ring-app-primary-10"
            />
          </div>
        </div>
      )}

      {tab === 'members' && (
        <div>
          {/* Add Members Header */}
          <div className="rounded-2xl border border-app-grey-25 bg-app-white-100 p-4">
            <div className="text-sm font-semibold text-app-black-100">Add Members</div>
            <div className="mt-1 text-xs text-app-grey-60">Select people to add to this group</div>

            {/* Search */}
            <div className="relative mt-3">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-app-grey-50" />
              <input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Find members"
                className="h-10 w-full rounded-xl border border-app-grey-25 bg-app-white-100 pl-10 pr-3 text-sm text-app-black-100 placeholder:text-app-grey-50 outline-none focus:border-app-primary-60 focus:ring-2 focus:ring-app-primary-10"
              />
            </div>

            {/* Unified members list */}
            <div className="mt-3 max-h-96 overflow-y-auto">
              {isLoadingUsers ? (
                <div className="flex items-center justify-center py-12">
                  <div className="flex flex-col items-center gap-3">
                    <div className="h-8 w-8 animate-spin rounded-full border-4 border-app-grey-30 border-t-app-primary-60"></div>
                    <div className="text-sm text-app-grey-60">Loading members...</div>
                  </div>
                </div>
              ) : (
                <>
                  {users
                    .filter((user) => user.label.toLowerCase().includes(searchQuery.toLowerCase()))
                    .map((user) => {
                      const isSelected = selectedUsers.has(user.value)
                      return (
                        <button
                          type="button"
                          key={user.value}
                          onClick={() => {
                            if (isSelected) {
                              handleRemoveMember(user.value)
                            } else {
                              handleAddMember(user.value)
                            }
                          }}
                          className="flex w-full items-center justify-between rounded-xl px-2 py-2 text-left hover:bg-app-grey-10"
                        >
                          <div className="flex items-center gap-3">
                            <AppAvatar
                              size="sm"
                              type="mixed"
                              shape="Rounded"
                              text={getInitials({
                                firstName: user.user?.firstName || '',
                                lastName: user.user?.lastName || '',
                              })}
                            />
                            <div className="leading-tight">
                              <div className="text-sm font-semibold text-app-black-100">
                                {user.label}
                              </div>
                              {user.email ? (
                                <div className="text-xs text-app-grey-60">{user.email}</div>
                              ) : (
                                <div className="text-xs text-app-grey-60">Member</div>
                              )}
                            </div>
                          </div>

                          {/* Checkbox */}
                          <div
                            className={[
                              'grid h-5 w-5 place-items-center rounded border',
                              isSelected
                                ? 'border-app-primary-60 bg-app-primary-60'
                                : 'border-app-grey-40 bg-app-white-100',
                            ].join(' ')}
                          >
                            {isSelected && (
                              <Check
                                className="h-3 w-3 !text-white"
                                strokeWidth={3}
                                stroke="white"
                              />
                            )}
                          </div>
                        </button>
                      )
                    })}

                  {users.filter((user) =>
                    user.label.toLowerCase().includes(searchQuery.toLowerCase())
                  ).length === 0 && (
                    <div className="py-8 text-center text-sm text-app-grey-60">
                      {searchQuery ? 'No users found' : 'No members available'}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between border-t border-app-grey-25 px-5 py-4">
        <button
          type="button"
          onClick={onClose}
          className="rounded-xl border border-app-grey-25 bg-app-white-100 px-4 py-2 text-sm font-semibold text-app-grey-80 hover:bg-app-grey-10"
        >
          Cancel
        </button>

        <button
          type="button"
          onClick={handleCreateGroup}
          disabled={isCreateDisabled}
          className="rounded-xl bg-app-primary-60 px-4 py-2 text-sm font-semibold text-app-white-100 hover:bg-app-primary-70 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isCreating ? 'Creating group...' : 'Create group'}
        </button>
      </div>
    </AppModalBox>
  )
}

export default GroupCreation

function TabButton({
  active,
  children,
  onClick,
}: {
  active?: boolean
  children: React.ReactNode
  onClick?: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'relative pb-3 text-sm font-semibold',
        active ? 'text-app-primary-60' : 'text-app-grey-60 hover:text-app-grey-80',
      ].join(' ')}
    >
      {children}
      <span
        className={[
          'absolute -bottom-[1px] left-0 h-[2px] w-full rounded-full transition',
          active ? 'bg-app-primary-60' : 'bg-transparent',
        ].join(' ')}
      />
    </button>
  )
}
